"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { Container, Title, Text, Paper, Alert, Center, Button, TextInput, PasswordInput, Stack, SocialLoginButtons } from "@forestsync/ui";
import { useTranslations } from "next-intl";
import { registerAction } from "@/actions/register";
import { loginWithOAuthAction } from "@/actions/auth";
import { useAnalytics } from "@/components/analytics/AnalyticsProvider";
import Link from "next/link";
import { TbAlertCircle, TbUser, TbLock, TbLogin, TbInfoCircle } from "@forestsync/ui";

export default function RegisterPage() {
  const t = useTranslations("auth");
  const { trackEvent } = useAnalytics();
  const searchParams = useSearchParams();
  const oauthError = searchParams.get("oauthError") === "true";
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRegister = async (formData: FormData) => {
    setError(null);

    // Client-side password match validation
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    if (password !== confirmPassword) {
      setError(t("passwordsDoNotMatch"));
      return;
    }

    setLoading(true);

    try {
      const result = await registerAction(formData);
      if (result?.error) {
        setError(result.error);
      } else if (result?.success) {
        setSuccess(true);
        trackEvent({ name: "sign_up", method: "email" });
      }
    } catch (err) {
      setError(t("unknownError"));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container size="xs" py="xl">
        <Paper withBorder shadow="md" p="xl" radius="md">
          <Center mb="md">
            <TbAlertCircle size={48} className="text-blue-500" />
          </Center>
          <Title order={2} ta="center" mb="sm">
            {t("checkEmail")}
          </Title>
          <Text c="dimmed" ta="center" mb="xl">
            {t("checkEmailMessage")}
          </Text>
          <Button fullWidth component={Link} href="/login">
            {t("loginLink")}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" py="xl">
      <Center mb="xl">
        <div>
          <Title order={1} ta="center">{t("registerTitle")}</Title>
          <Text c="dimmed" ta="center" mt="sm">
            {t("registerSubtitle")}
          </Text>
        </div>
      </Center>

      <Paper withBorder shadow="md" p="xl" radius="md">
        {oauthError && (
          <Alert icon={<TbInfoCircle size={16} />} color="blue" mb="md" variant="light">
            {t("oauthServerError")}
          </Alert>
        )}
        {error && (
          <Alert icon={<TbAlertCircle size={16} />} color="red" mb="md" variant="light">
            {error}
          </Alert>
        )}

        <form action={handleRegister}>
          <Stack gap="md">
            <TextInput
              name="email"
              label={t("email")}
              placeholder={t("enterEmail")}
              leftSection={<TbUser size={16} />}
              required
              size="md"
              disabled={loading}
            />

            <PasswordInput
              name="password"
              label={t("password")}
              placeholder={t("enterPassword")}
              leftSection={<TbLock size={16} />}
              required
              minLength={12}
              size="md"
              disabled={loading}
            />

            <PasswordInput
              name="confirmPassword"
              label={t("confirmPassword")}
              placeholder={t("enterConfirmPassword")}
              leftSection={<TbLock size={16} />}
              required
              size="md"
              disabled={loading}
            />

            <Button
              type="submit"
              leftSection={<TbLogin size={16} />}
              loading={loading}
              size="md"
              fullWidth
              className="mt-2"
            >
              {loading ? t("registering") : t("registerButton")}
            </Button>
          </Stack>
        </form>

        <SocialLoginButtons
          oauthAction={loginWithOAuthAction}
          translations={{
            orContinueWith: t("orContinueWith"),
            signInWithGoogle: t("signInWithGoogle"),
            signInWithMicrosoft: t("signInWithMicrosoft"),
          }}
        />

        <Text ta="center" mt="xl" size="sm">
          {t("hasAccount")}{" "}
          <Text component={Link} href="/login" c="blue" fw={500}>
            {t("loginLink")}
          </Text>
        </Text>

      </Paper>
    </Container>
  );
}
